import uuid from 'uuid';

import {WIZARD_GROUP_TYPES} from "./WizardItem/wizardItemInterface";

/**
 * returns current wizard step section items
 * @param source {string}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns array {[wizardGroupInterface]}
 */
export const getCurrentGroupsFromSource = (source, wizardActiveItem) => {
  if (!wizardActiveItem) {
    return []
  }
  return wizardActiveItem.wizardGroups.filter(wizGroup => {
    return wizardActiveItem.wizardEdges.some(wizEdge => wizEdge.source === source && wizEdge.target === wizGroup.id && wizGroup.enabled)
  });
}

/**
 * returns current wizard step section items
 * @param siblingGroups {[wizardGroupInterface]}
 * @returns boolean
 */
export const checkForSameSiblingsGroups = (siblingGroups) => {
  return siblingGroups.length > 1 && siblingGroups.every(siblingGroup => {
    return (siblingGroup.nodeType === WIZARD_GROUP_TYPES.group) && (siblingGroup.groupName === siblingGroups[0].groupName)
  })
}

/**
 * this returns on qkb wizard custom groups or feature groups as features
 * @param currentGroups {[wizardGroupInterface]}
 * @returns {boolean}
 */
export const displayMaterialsWizardGroupAsFeature = (currentGroups) => {
  return currentGroups.length > 1 || (currentGroups.length === 1 && currentGroups[0].nodeType === WIZARD_GROUP_TYPES.custom)
}

/**
 * returns current wizard step section items
 * @param wizardGroup {wizardGroupInterface}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns object
 */
export const getWizardGroupLogic = (wizardGroup, wizardActiveItem) => {
  const sourceEdge = wizardActiveItem.wizardEdges.find(wizEdge => wizEdge.target === wizardGroup.id && (
    wizEdge.source === '0' || (wizardActiveItem.wizardGroups && wizardActiveItem.wizardGroups.some(wizGroup => wizGroup.id === wizEdge.source))
  ));
  const siblingGroups = sourceEdge ? getCurrentGroupsFromSource(sourceEdge.source, wizardActiveItem) : [];
  const areSameSiblingGroups = checkForSameSiblingsGroups(siblingGroups);
  const productSectionDisabled = !displayMaterialsWizardGroupAsFeature(siblingGroups)
    || (siblingGroups.length > 1 && areSameSiblingGroups); // this will be one merged group, no need to show product

  return {sourceEdge, siblingGroups, areSameSiblingGroups, productSectionDisabled}
}


/**
 * returns updated list with hashes
 * @param wizardList {[wizardItemInterface]}
 * @returns [wizardItemInterface]
 */
export const getWizardListWithHashes = (wizardList = []) => {
  return wizardList.map(wizardItem => {
    if (!wizardItem.uniqueHashId) {
      wizardItem.uniqueHashId = uuid.v4(); // is used for clone comparing
    }
    return wizardItem
  })
}

/**
 * @param feature {featureInterface}
 * @param wizardPopupState {object}
 * @returns boolean
 */
export const getFeatureIsChecked = (feature, {featuresExcluded, featuresIncluded, useIncluded}) => {
  return useIncluded
    ? getFeatureIncludedIndex(feature, featuresIncluded) > -1
    : getFeatureExcludedIndex(feature, featuresExcluded) < 0
}

/**
 * @param feature {featureInterface}
 * @param featuresExcluded {array}
 * @returns number
 */
export const getFeatureExcludedIndex = (feature, featuresExcluded = []) => {
  return featuresExcluded.findIndex(excluded => {
    if (!excluded) {
      return false
    }
    if (typeof excluded === "object") { // new logic for excluded
      // return (feature.name && excluded.name && (feature.name === excluded.name))
      //   || (feature.sku && excluded.sku && (feature.sku === excluded.sku))
      //   || (feature.sharedFeatureName && excluded.sharedFeatureName && (feature.sharedFeatureName === excluded.sharedFeatureName))
      return feature.featureDefinitionId === excluded.featureDefinitionId
    }
    return feature.sku === excluded || feature.name === excluded
  })
}

/**
 * @param feature {featureInterface}
 * @param featuresIncluded {array}
 * @returns number
 */
export const getFeatureIncludedIndex = (feature, featuresIncluded = []) => {
  const lowerCasedName = feature.sharedFeatureName ? feature.sharedFeatureName.toLowerCase() : "";
  return featuresIncluded.findIndex(included => (feature.featureDefinitionId === included.featureDefinitionId)
    || (lowerCasedName && included.sharedFeatureName && (included.sharedFeatureName.toLowerCase() === lowerCasedName)))
}

/**
 * @param featuresExcluded {array}
 * @param filteredFeatures {array}
 * @returns array
 */
export const getNewExcludedFromFeatures = (featuresExcluded, filteredFeatures = []) => {
  return featuresExcluded.filter(excluded => {
    if (excluded && (typeof excluded === "object")) { // new logic for excluded // added null check (typeof null is "object" in JS)
      return !filteredFeatures.some(feature => {
        // return (feature.name && excluded.name && (feature.name === excluded.name))
        //   || (feature.sku && excluded.sku && (feature.sku === excluded.sku)))
        return feature.featureDefinitionId === excluded.featureDefinitionId
      })
    }
    return !filteredFeatures.some(feature => (feature.sku || feature.name) === excluded)
  })
}

/**
 * @param featuresIncluded {array}
 * @param filteredFeatures {array}
 * @returns array
 */
export const getIncludedFromFeatures = (featuresIncluded, filteredFeatures = []) => {
  return featuresIncluded.filter(included => {
    return !filteredFeatures.some(feature => feature.featureDefinitionId === included.featureDefinitionId)
  })
}

/**
 * @param feature {featureInterface}
 * @returns wizardExcludedFeatureInterface
 */
export const getFeaturesExcludedObject = (feature) => {
  return {
    sku: feature.sku,
    name: feature.name,
    sharedFeatureName: feature.sharedFeatureName,
    featureDefinitionId: feature.featureDefinitionId
  }
}

const recursivelySetRoomProperty = (wizardGroup, wizardItem) => {
  const sourceEdges = wizardItem.wizardEdges.filter(edge => edge.source === wizardGroup.id);
  if (!sourceEdges.length) {
    return
  }
  sourceEdges.forEach(edge => {
    const targetWizGroup = wizardItem.wizardGroups.find(wizGroup => wizGroup.id === edge.target);
    if (targetWizGroup) {
      targetWizGroup.visibleInVisualizers = wizardGroup.visibleInVisualizers;
      recursivelySetRoomProperty(targetWizGroup, wizardItem);
    }
  })
}

export const setWizardItemsWizardGroupsRooms = (wizardList) => { // not used anymore
  wizardList = wizardList.map(wizardItem => {
    const topRooms = wizardItem.wizardGroups.filter(wizGroup => wizGroup.groupName === "Room");
    topRooms.forEach(roomWg => {
      if (roomWg.visibleInVisualizers) {
        recursivelySetRoomProperty(roomWg, wizardItem)
      }
    })
    return wizardItem
  });
  return wizardList
}

export const setWizardItemWizardGroupsRooms = (wizardItem, roomWg) => {
  if (roomWg.visibleInVisualizers) {
    recursivelySetRoomProperty(roomWg, wizardItem)
  }
  return wizardItem
}
